import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../common/Layout";
import { usePageMeta } from "../../common/usePageMeta";
import DeliveryCountdown from "../../common/DeliveryCountdown";
import { useDeliveryStatus, formatResume } from "../../common/deliveryStatus";
import { CART_KEY, readCart, writeCart, addToCart } from "./cart";
import { track } from "../../features/analytics/tracker";
import { getStock, getStockStatus, getStartingPrice, minPackGrams, packPrice } from "../../assets/utils/productUtils";

/* ─── constants ───────────────────────────────────────────────────────────── */
const FREE_DELIVERY_THRESHOLD = 6.99; // any bag qualifies on its own
const LOCAL_DELIVERY_FEE      = 1.49;
const LATE_DELIVERY_FEE = 1.00;

/* ─── helpers ────────────────────────────────────────────────────────────── */
// Card checkout holds the cart's stock server-side while the customer is on
// Stripe. The hold is remembered for this tab so that coming back to the cart
// (cancel link, back button) gives the stock back instead of the customer's
// own hold making their items look sold out.
const HOLD_KEY = "td:checkoutHold";
function readStoredHold() {
  try { return JSON.parse(sessionStorage.getItem(HOLD_KEY) || "null"); } catch { return null; }
}
function storeHold(hold) {
  try { hold ? sessionStorage.setItem(HOLD_KEY, JSON.stringify(hold)) : sessionStorage.removeItem(HOLD_KEY); } catch { /* storage blocked */ }
}
async function releaseStoredHold() {
  const hold = readStoredHold();
  if (!hold) return;
  storeHold(null);
  try {
    await fetch("https://teardrophydro.co.uk/garageFarm/checkout-hold/release", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(hold),
    });
  } catch { /* expires on its own */ }
}

function maxQtyForItem(it) {
  if (it.availableGrams && it.grams) return Math.floor(it.availableGrams / it.grams);
  if (it.availableUnits)             return it.availableUnits;
  return Infinity;
}

function itemHasStockProblem(it) {
  return it.qty > maxQtyForItem(it);
}

const hasExt = (s = "") => /\.[a-z0-9]{2,5}$/i.test(s);
const FIRST_IN_FOLDER = {
  genoBasil: "IMG_5987.JPEG", chives: "IMG_6392.JPEG", cress: "IMG_4581.JPEG",
  dill: "IMG_6425.JPEG", komatsuna: "IMG_4483.JPEG", lettuce: "IMG_4447.JPEG",
  mix: "IMG_4391.JPEG", seedlings: "IMG_4469.JPEG", sorrel: "IMG_5044.JPEG",
  sunflowers: "sun2.png", mint: "IMG_6066.JPEG", salvoBasil: "IMG_6629.JPEG",
  coriander: "IMG_6755.JPEG", mizuna: "IMG_6786.JPEG", frenchMix: "IMG_6777.JPEG",
  mesclunMix: "IMG_6779.JPEG",
};

async function resolveFolderFirstImage(folderUrl) {
  const baseNoSlash = folderUrl.endsWith("/") ? folderUrl.slice(0, -1) : folderUrl;
  const folder = baseNoSlash.split("/").pop();
  const listed = FIRST_IN_FOLDER[folder];
  if (listed) return `${baseNoSlash}/${listed}`;
  return `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 3'><rect width='100%' height='100%' fill='%23eef3f9'/></svg>`;
}

function isAvailableNow(p) {
  if (p?.inStock) return true;
  return getStockStatus(p) === "AVAILABLE";
}

function enrichWithLiveStock(items, liveProducts) {
  if (!liveProducts?.length) return items;
  const byId = new Map(liveProducts.map(p => [String(p.id), p]));
  return items.map(it => {
    const productId = String(it.baseId || it.id).split("-")[0];
    const live = byId.get(productId);
    if (!live) return it;
    // Re-price packs at today's price, so an item added before a price change
    // still shows (and is sent to checkout at) the same price as the product page.
    const isPack = it.grams > 0 && String(it.per || "").toLowerCase() !== "100g";
    const price  = isPack && live.price ? packPrice(live, it.grams) : it.price;
    const liveGrams = live.stock ?? live.stockGrams ?? live.quantity ?? null;
    if (liveGrams === null) return { ...it, price };
    return { ...it, price, availableGrams: liveGrams };
  });
}

// Mirrors DiscountCode.calcDiscount on the server, so the discount follows the
// basket when it changes after a code was applied. The server works it out
// again at checkout and refuses the order if the totals don't match.
function discountFor(promo, subtotal, delivery) {
  if (!promo) return 0;
  const value = Number(promo.value) || 0;
  switch (promo.type) {
    case "percent":
    case "first_order_percent": return Number(((subtotal * value) / 100).toFixed(2));
    case "fixed":
    case "first_order_fixed":   return Math.min(Number(value.toFixed(2)), subtotal);
    case "free_delivery":       return Number((delivery || 0).toFixed(2));
    default:                    return promo.discountAmount || 0;
  }
}

/* ─── Phone validation helper ─────────────────────────────────────────────── */
function isValidUKPhone(val) {
  return /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3,4}$/.test(val.trim());
}

/* ─── Shared input style helper ──────────────────────────────────────────── */
function fieldStyle(hasError = false) {
  return {
    width: "100%", boxSizing: "border-box",
    padding: "var(--s-3)", fontSize: 13,
    border: `1px solid ${hasError ? "#e53e3e" : "var(--border)"}`,
    borderRadius: "var(--r-sm)", fontFamily: "inherit",
    background: "var(--surface)", color: "var(--text)",
  };
}

/* ─── Free Delivery Progress Bar ────────────────────────────────────────── */
function FreeDeliveryBar({ subtotal }) {
  const remaining = Math.max(0, FREE_DELIVERY_THRESHOLD - subtotal);
  const pct       = Math.min(100, (subtotal / FREE_DELIVERY_THRESHOLD) * 100);
  const achieved  = subtotal >= FREE_DELIVERY_THRESHOLD;

  return (
    <div className="free-delivery-bar" aria-live="polite">
      {achieved ? (
        <p className="free-delivery-bar__label free-delivery-bar__label--achieved">
          🎉 You've unlocked free delivery!
        </p>
      ) : (
        <p className="free-delivery-bar__label">
          Add <strong>£{remaining.toFixed(2)}</strong> more for free delivery
        </p>
      )}
      <div className="free-delivery-bar__track" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`free-delivery-bar__fill ${achieved ? "free-delivery-bar__fill--achieved" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/* ─── Trust Block ────────────────────────────────────────────────────────── */
function TrustBlock() {
  return (
    <div className="cart-trust-block">
      <div className="cart-trust-row">
        <span className="cart-trust-icon">⏱️</span>
        <span>Harvested fresh on the day of your order - never stored</span>
      </div>
      <div className="cart-trust-row">
        <span className="cart-trust-icon">🚚</span>
        <span>Personally delivered by Michael within ~3 miles of the farm</span>
      </div>
      <div className="cart-trust-row">
        <span className="cart-trust-icon">📞</span>
        <span>Questions? Call or text: <a href="tel:07541264513" style={{ color: "var(--brand-2)", fontWeight: 600 }}>07541 264513</a></span>
      </div>
    </div>
  );
}

/* ─── Recommendation Card ────────────────────────────────────────────────── */
function RecommendationCard({ product, img, onAdd }) {
  const [addState, setAddState] = useState("idle");
  const start = getStartingPrice(product);

  const handleAdd = () => {
    if (addState !== "idle") return;
    setAddState("adding");

    const grams          = minPackGrams(product);
    const price          = packPrice(product, grams);
    const availableGrams = getStock(product);

    addToCart({
      id:            `${product.id}-${grams}`,
      baseId:        product.id,
      name:          `${product.name} (${grams}g)`,
      price,
      grams,
      per:           "pack",
      image:         img || product.image,
      availableGrams,
      qty:           1,
    });

    track("rec_add_to_cart", { productId: product.id, name: product.name, grams, price });
    onAdd?.();

    setTimeout(() => setAddState("done"), 300);
    setTimeout(() => setAddState("idle"), 2000);
  };

  return (
    <div className="rec-card">
      <Link to={`/product/${product.id}`} className="rec-card__media" style={{ backgroundImage: `url(${img})` }} />
      <div className="rec-card__body">
        <Link to={`/product/${product.id}`} className="rec-card__name">{product.name}</Link>
        <span className="rec-card__price">from £{start.price}</span>
      </div>
      <button
        className={`rec-card__btn ${addState === "done" ? "rec-card__btn--done" : ""}`}
        onClick={handleAdd}
        disabled={addState === "adding"}
        aria-label={`Add ${product.name} to cart`}
      >
        {addState === "done" ? "✓" : "+"}
      </button>
    </div>
  );
}

/* ─── Recommendations Section ────────────────────────────────────────────── */
function Recommendations({ cartItemIds, subtotal, liveProducts, onCartChange }) {
  const [imgCache, setImgCache] = useState({});

  const recommendations = useMemo(() => {
    const remaining = FREE_DELIVERY_THRESHOLD - subtotal;
    if (remaining <= 0) return [];

    return liveProducts
      .filter(p => p?.enabled !== false && isAvailableNow(p))
      .filter(p => !cartItemIds.has(String(p.id)))
      .map(p => ({ product: p, start: getStartingPrice(p) }))
      .sort((a, b) => {
        const diffA = Math.abs(remaining - Number(a.start.price));
        const diffB = Math.abs(remaining - Number(b.start.price));
        return diffA - diffB;
      })
      .slice(0, 4);
  }, [liveProducts, cartItemIds, subtotal]);

  useEffect(() => {
    (async () => {
      const updates = {};
      for (const { product: p } of recommendations) {
        if (imgCache[p.id]) continue;
        const src = String(p.image || "");
        if (!src) continue;
        updates[p.id] = hasExt(src) ? src : await resolveFolderFirstImage(src);
      }
      if (Object.keys(updates).length) setImgCache(prev => ({ ...prev, ...updates }));
    })();
  }, [recommendations]);

  if (!recommendations.length) return null;

  return (
    <div className="rec-section">
      <p className="rec-section__heading">🌿 You might also like</p>
      <div className="rec-list">
        {recommendations.map(({ product }) => (
          <RecommendationCard
            key={product.id}
            product={product}
            img={imgCache[product.id] || product.image}
            onAdd={onCartChange}
          />
        ))}
      </div>
    </div>
  );
}

export function PromoCodeBox({ subtotal, deliveryCost, onApply, onRemove, appliedCode }) {
  const [input,   setInput]   = useState(appliedCode || '');
  const [status,  setStatus]  = useState('idle'); // 'idle'|'checking'|'ok'|'err'
  const [message, setMessage] = useState('');
 
  // If parent clears the code (e.g. cart empties), reset local state
  useEffect(() => {
    if (!appliedCode) { setInput(''); setStatus('idle'); setMessage(''); }
  }, [appliedCode]);
 
  const validate = async () => {
    const code = input.trim().toUpperCase();
    if (!code) return;
    setStatus('checking');
    setMessage('');
    try {
      const res = await fetch('https://teardrophydro.co.uk/garageFarm/discounts/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, subtotal, deliveryCost }),
      });
      const data = await res.json();
      if (data.valid) {
        setStatus('ok');
        setMessage(data.message || `Code applied`);
        onApply({
          code:           data.code,
          discountAmount: data.discountAmount,
          freeDelivery:   data.freeDelivery,
          type:           data.type,
          value:          data.value,
        });
      } else {
        setStatus('err');
        setMessage(data.message || 'Invalid code');
        onRemove();
      }
    } catch {
      setStatus('err');
      setMessage('Could not check that code. Please try again.');
      onRemove();
    }
  };
 
  const remove = () => {
    setInput(''); setStatus('idle'); setMessage('');
    onRemove();
  };
 
  return (
    <div style={{ marginTop: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 'var(--s-2)', color: 'var(--text)' }}>
        Discount / referral code
      </label>
 
      {/* Input row */}
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          type="text"
          value={input}
          onChange={(e) => {
            setInput(e.target.value.toUpperCase());
            if (status !== 'idle') { setStatus('idle'); setMessage(''); onRemove(); }
          }}
          onKeyDown={(e) => e.key === 'Enter' && validate()}
          placeholder="Enter code…"
          disabled={status === 'ok'}
          style={{
            flex: 1, padding: 'var(--s-3)', fontSize: 13,
            border: `1px solid ${status === 'ok' ? '#38a169' : status === 'err' ? '#e53e3e' : 'var(--border)'}`,
            borderRadius: 'var(--r-sm)',
            background: status === 'ok' ? '#f0fff4' : 'var(--surface)',
            color: 'var(--text)',
            textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600,
          }}
        />
        {status === 'ok' ? (
          <button
            onClick={remove}
            style={{
              padding: 'var(--s-3) var(--s-4)', borderRadius: 'var(--r-sm)',
              border: '1px solid #e2e8f0', background: '#fff',
              color: '#718096', fontSize: 13, cursor: 'pointer', fontWeight: 600,
            }}
          >Remove</button>
        ) : (
          <button
            onClick={validate}
            disabled={!input.trim() || status === 'checking'}
            style={{
              padding: 'var(--s-3) var(--s-4)', borderRadius: 'var(--r-sm)',
              border: 'none', background: 'var(--brand-2, #38a169)',
              color: '#fff', fontSize: 13, cursor: input.trim() ? 'pointer' : 'not-allowed',
              fontWeight: 600, opacity: !input.trim() ? 0.55 : 1,
            }}
          >
            {status === 'checking' ? '…' : 'Apply'}
          </button>
        )}
      </div>
 
      {/* Feedback message */}
      {message && (
        <p style={{
          margin: 'var(--s-2) 0 0', fontSize: 12, fontWeight: 600,
          color: status === 'ok' ? '#276749' : '#c53030',
        }}>
          {status === 'ok' ? '✓ ' : '✕ '}{message}
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CartPage
═══════════════════════════════════════════════════════════════════════════ */
export default function CartPage() {
  usePageMeta({ title: 'Your Basket | TearDrop Hydro', path: '/cart', noindex: true });
  const navigate = useNavigate();
  const [rawItems,     setRawItems]     = useState(readCart());
  const [liveProducts, setLiveProducts] = useState([]);
  const [updating,     setUpdating]     = useState(false);
  const [checkingOut,  setCheckingOut]  = useState(false);
  const [err, setErr] = useState("");

  const [paymentMethod,        setPaymentMethod]        = useState("card");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [cashName,     setCashName]     = useState("");
  const [cashPhone,    setCashPhone]    = useState("");
  const [cashEmail,    setCashEmail]    = useState("");
  const [cashLine1,    setCashLine1]    = useState("");
  const [cashLine2,    setCashLine2]    = useState("");
  const [cashCity,     setCashCity]     = useState("");
  const [cashPostcode, setCashPostcode] = useState("");
  const [phoneErr,     setPhoneErr]     = useState("");
  const [deliverySlot, setDeliverySlot] = useState("evening"); // "" | "morning" | "evening" | "late"
  const [lateNote,     setLateNote]     = useState("");
  const [promoCode,   setPromoCode]    = useState(null);
  // Mailing-list opt-in, ticked by default (PECR soft opt-in: they are buying
  // from us and every email carries an unsubscribe link). The server only ever
  // adds a new subscriber, so an existing opt-out is never overwritten.
  const [joinMailingList, setJoinMailingList] = useState(true);

  const { status: deliveryStatus } = useDeliveryStatus();
  // A delivery pause never blocks checkout; it only means the order arrives a
  // little later. `slotDeferred` = this window's next round falls in a pause.
  const slotDeferred = slot => !!deliveryStatus?.windows?.[slot]?.deferred;
  const slotDeliveryDate = slot =>
    deliveryStatus?.windows?.[slot]?.delivery || deliveryStatus?.nextAvailable?.date || null;

  useEffect(() => {
    const cart = readCart();
    const subtotal = cart.reduce((s, it) => s + (it.price || 0) * (it.qty || 0), 0);
    track("cart_view", { item_count: cart.length, subtotal: +subtotal.toFixed(2) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await releaseStoredHold(); // before reading stock, so our own hold isn't counted
        const res  = await fetch("https://teardrophydro.co.uk/garageFarm/products");
        const data = await res.json();
        if (alive) setLiveProducts(Array.isArray(data) ? data : []);
      } catch {
        // silently degrade
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onStorage = e => { if (e.key === CART_KEY) setRawItems(readCart()); };
    const onEvt     = e => setRawItems(e.detail || readCart());
    window.addEventListener("storage", onStorage);
    window.addEventListener("cart:updated", onEvt);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("cart:updated", onEvt);
    };
  }, []);

  const refreshCart = () => setRawItems(readCart());

  const items = useMemo(
    () => enrichWithLiveStock(rawItems, liveProducts),
    [rawItems, liveProducts]
  );

  const totals = useMemo(() => {
    // Rounded to pence so float drift can't drop a £6.99 basket below the threshold
    const subtotal = Math.round(items.reduce((sum, it) => sum + (it.price || 0) * (it.qty || 0), 0) * 100) / 100;
    const delivery = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : (subtotal > 0 ? LOCAL_DELIVERY_FEE : 0);
    const lateFee  = deliverySlot === "late" ? LATE_DELIVERY_FEE : 0;
    const discount = Math.min(discountFor(promoCode, subtotal, delivery), subtotal + delivery + lateFee);
    const rawDelivery = deliverySlot === "late" ? LATE_DELIVERY_FEE : (subtotal > 0 ? LOCAL_DELIVERY_FEE : 0);
    const grand    = Math.round((subtotal + delivery + lateFee - discount) * 100) / 100;
    return { subtotal, delivery, rawDelivery, lateFee, discount, grand };
  }, [items, deliverySlot, promoCode]);

  const cartItemIds = useMemo(
    () => new Set(items.map(it => String(it.baseId || it.id))),
    [items]
  );

  const stockProblems   = useMemo(() => items.filter(itemHasStockProblem), [items]);
  const hasStockProblem = stockProblems.length > 0;

  const changeQty = (id, delta) => {
    setUpdating(true);
    setRawItems(prev => {
      const enriched = items.find(it => it.id === id);
      const max = enriched ? maxQtyForItem(enriched) : Infinity;
      const next = prev.map(it => {
        if (it.id !== id) return it;
        const qty = Math.min(max === Infinity ? Infinity : max, Math.max(1, it.qty + delta));
        if (qty !== it.qty && enriched) {
          track("cart_quantity_change", { productId: id, name: enriched.name, from: it.qty, to: qty });
        }
        return { ...it, qty };
      });
      writeCart(next);
      return next;
    });
    setTimeout(() => setUpdating(false), 150);
  };

  const setQtyDirect = (id, rawQty) => {
    setUpdating(true);
    setRawItems(prev => {
      const enriched = items.find(it => it.id === id);
      const max = enriched ? maxQtyForItem(enriched) : Infinity;
      const next = prev.map(it => {
        if (it.id !== id) return it;
        const qty = Math.min(max === Infinity ? 9999 : max, Math.max(1, Number(rawQty) || 1));
        if (qty !== it.qty && enriched) {
          track("cart_quantity_change", { productId: id, name: enriched.name, from: it.qty, to: qty });
        }
        return { ...it, qty };
      });
      writeCart(next);
      return next;
    });
    setTimeout(() => setUpdating(false), 150);
  };

  const removeItem = id => {
    setUpdating(true);
    setRawItems(prev => {
      const removed = items.find(it => it.id === id) || prev.find(it => it.id === id);
      if (removed) {
        track("remove_from_cart", { productId: id, name: removed.name, price: removed.price, qty: removed.qty });
      }
      const next = prev.filter(it => it.id !== id);
      writeCart(next);
      return next;
    });
    setTimeout(() => setUpdating(false), 150);
  };

  const clearCart = () => {
    setUpdating(true);
    writeCart([]); setRawItems([]);
    setTimeout(() => setUpdating(false), 150);
  };

  const clampAll = () => {
    setUpdating(true);
    setRawItems(prev => {
      const next = prev.map(rawIt => {
        const enriched = items.find(it => it.id === rawIt.id) || rawIt;
        const max = maxQtyForItem(enriched);
        return rawIt.qty > max ? { ...rawIt, qty: max } : rawIt;
      });
      writeCart(next);
      return next;
    });
    setTimeout(() => setUpdating(false), 150);
  };

  function buildCheckoutCartPayload(list) {
    return (list || []).map(it => {
      const isPer100g = String(it.per || "").toLowerCase() === "100g";
      return {
        id: it.id, name: it.name, price: it.price,
        stockGrams: isPer100g,
        quantity: isPer100g ? it.qty * 100 : it.qty,
      };
    });
  }

  // ── Card checkout (Stripe) ─────────────────────────────────────────────────
  const checkoutCard = async () => {
    setErr("");
    if (!deliverySlot) {
      setErr("Please select a delivery window before checking out.");
      return;
    }
    track("checkout_initiated", { method: "card", total: totals.grand, items: items.length });

    try {
      setCheckingOut(true);
      const cart = buildCheckoutCartPayload(items);
      if (totals.delivery > 0) {
        cart.push({ id: "delivery-local", name: "Local delivery", price: totals.delivery, stockGrams: false, quantity: 1, isDelivery: true });
      }
      if (deliverySlot === "late") {
        cart.push({ id: "late-delivery-fee", name: "Late delivery (7:00pm–9:00pm)", price: LATE_DELIVERY_FEE, stockGrams: false, quantity: 1, isDelivery: true });
      }
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart,
          deliveryInstructions: deliveryInstructions.trim() || null,
          deliverySlot:         deliverySlot || null,
          lateNote:             lateNote.trim() || null,
          promoCode:           promoCode?.code || null,
          expectedTotal:        totals.grand, // server refuses the checkout if its total differs
          marketingOptIn:       joinMailingList,
          previousHold:         readStoredHold(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Checkout failed (${res.status})`);
      }
      const data = await res.json();
      if (!data?.url) throw new Error("No checkout URL returned");
      storeHold(data.hold || null);
      track("checkout_redirect", { method: "card", total: totals.grand, items: items.length });
      sessionStorage.setItem("td:lastCartTotal", String(totals.grand.toFixed(2)));
      sessionStorage.setItem("td:lastDeliverySlot", deliverySlot);
      window.location.assign(data.url);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Something went wrong starting checkout.");
    } finally {
      setCheckingOut(false);
    }
  };

  // ── Cash on delivery ───────────────────────────────────────────────────────
  const checkoutCash = async () => {
    setErr("");
    setPhoneErr("");
    if (!deliverySlot) {
      setErr("Please select a delivery window before checking out.");
      return;
    }

    if (!cashName.trim())     { setErr("Please enter your name.");             return; }
    if (!cashPhone.trim())    { setErr("Please enter your phone number.");      return; }
    if (!cashEmail.trim())    { setErr("Please enter your email address.");     return; }
    if (!cashLine1.trim())    { setErr("Please enter your delivery address.");  return; }
    if (!cashCity.trim())     { setErr("Please enter your town or city.");      return; }
    if (!cashPostcode.trim()) { setErr("Please enter your postcode.");          return; }
    if (!isValidUKPhone(cashPhone)) {
      setPhoneErr("Please enter a valid UK mobile number.");
      return;
    }

    track("checkout_initiated", { method: "cash", total: totals.grand, items: items.length });

    try {
      setCheckingOut(true);
      const cart = buildCheckoutCartPayload(items);
      if (totals.delivery > 0) {
        cart.push({ id: "delivery-local", name: "Local delivery", price: totals.delivery, stockGrams: false, quantity: 1, isDelivery: true });
      }
      if (deliverySlot === "late") {
        cart.push({ id: "late-delivery-fee", name: "Late delivery (7:00pm–9:00pm)", price: LATE_DELIVERY_FEE, stockGrams: false, quantity: 1, isDelivery: true });
      }
      await releaseStoredHold(); // a card checkout started earlier mustn't block this order
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/create-cash-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart,
          name:                 cashName.trim(),
          phone:                cashPhone.trim(),
          email:                cashEmail.trim(),
          address: {
            line1:    cashLine1.trim(),
            line2:    cashLine2.trim() || null,
            city:     cashCity.trim(),
            postcode: cashPostcode.trim().toUpperCase(),
            country:  "GB",
          },
          deliveryInstructions: deliveryInstructions.trim() || null,
          deliverySlot:         deliverySlot || null,
          lateNote:             lateNote.trim() || null,
          total:                totals.grand,
          promoCode:           promoCode?.code || null,
          marketingOptIn:       joinMailingList,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Order failed (${res.status})`);
      }
      track("checkout_cash_placed", { total: totals.grand, items: items.length });
      sessionStorage.setItem("td:lastCartTotal", String(totals.grand.toFixed(2)));
      sessionStorage.setItem("td:lastDeliverySlot", deliverySlot);
      writeCart([]);
      navigate("/success?method=cash");
    } catch (e) {
      console.error(e);
      setErr(e.message || "Something went wrong placing your order.");
    } finally {
      setCheckingOut(false);
    }
  };

  const checkout = paymentMethod === "cash" ? checkoutCash : checkoutCard;

  const cashFieldsOk = paymentMethod === "card" || (
    cashName.trim().length > 0 &&
    cashPhone.trim().length > 0 &&
    cashEmail.trim().length > 0 &&
    cashLine1.trim().length > 0 &&
    cashCity.trim().length > 0 &&
    cashPostcode.trim().length > 0
  );

  const canCheckout = !hasStockProblem && !checkingOut && !updating && items.length > 0 && cashFieldsOk && deliverySlot !== "";
  const isEmpty     = items.length === 0;

  return (
    <Layout>
      <div className="cart-page container">
        <h1 className="h2" style={{ marginTop: 0 }}>Your cart</h1>

        {err && (
          <div className="notice" role="alert" style={{ marginBottom: "var(--s-4)", borderColor: "#ffd0d0", background: "#fff4f4" }}>
            <span>{err}</span>
          </div>
        )}

        {isEmpty ? (
          <div className="cart-empty">
            <p className="muted">Your cart is empty.</p>
            <div className="cta-row" style={{ marginTop: 10, justifyContent: "center" }}>
              <Link className="btn btn-primary" to="/browse-products">Browse products</Link>
            </div>
          </div>
        ) : (
          <div className="cart-grid">

            {/* ── Left: item list ── */}
            <div>
              {hasStockProblem && (
                <div className="notice" role="alert" style={{ marginBottom: "var(--s-4)", borderColor: "#f1b500", background: "#fff9e6", flexDirection: "column", alignItems: "flex-start", gap: "var(--s-3)" }}>
                  <div>
                    <strong style={{ color: "#7a5a0a" }}>⚠ Some items exceed available stock</strong>
                    <ul style={{ margin: "var(--s-2) 0 0", paddingLeft: "var(--s-4)", color: "var(--muted)", fontSize: 14, display: "grid", gap: 4 }}>
                      {stockProblems.map(it => {
                        const max = maxQtyForItem(it);
                        return (
                          <li key={it.id}>
                            <strong style={{ color: "var(--text)" }}>{it.name}</strong>
                            {" "}(you have {it.qty} in cart, only {max} available).{" "}
                            <button style={{ background: "none", border: "none", cursor: "pointer", color: "#7a5a0a", textDecoration: "underline", padding: 0, fontSize: "inherit", fontFamily: "inherit" }} onClick={() => setQtyDirect(it.id, max)}>
                              Set to {max}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="notice-actions">
                    <button className="btn btn-ghost" style={{ fontSize: 13 }} onClick={clampAll}>Fix all automatically</button>
                  </div>
                </div>
              )}

              <div className={`cart-list ${updating ? "is-updating" : ""}`}>
                {items.map(it => {
                  const max     = maxQtyForItem(it);
                  const problem = it.qty > max;
                  return (
                    <div key={it.id} className="cart-row" style={problem ? { borderColor: "#f1b500", background: "#fff9e6" } : undefined}>
                      <div className="cart-thumb" style={{ backgroundImage: `url(${it.image})` }} aria-hidden="true" />
                      <div className="cart-info">
                        <div className="cart-title">{it.name}</div>
                        <div className="cart-meta muted">£{it.price?.toFixed(2)} each</div>
                        {problem && (
                          <span className="badge badge-out" style={{ width: "fit-content", marginTop: 4 }}>⚠ Only {max} available</span>
                        )}
                        <div className="cart-controls">
                          <div className="cart-stepper">
                            <button className="cart-stepper__btn" onClick={() => changeQty(it.id, -1)} disabled={it.qty <= 1} aria-label="Decrease quantity">−</button>
                            <input type="number" className="cart-stepper__val" min={1} max={max === Infinity ? undefined : max} value={it.qty} onChange={e => setQtyDirect(it.id, e.target.value)} aria-label="Quantity" />
                            <button className="cart-stepper__btn" onClick={() => changeQty(it.id, 1)} disabled={it.qty >= max} aria-label="Increase quantity">+</button>
                          </div>
                          <button className="btn btn-ghost" style={{ fontSize: 13, minHeight: 36, padding: "0 var(--s-3)" }} onClick={() => removeItem(it.id)}>Remove</button>
                        </div>
                      </div>
                      <div className="cart-lineprice">£{(it.price * it.qty).toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="cart-actions">
                <button className="btn btn-ghost" onClick={clearCart}>Clear cart</button>
                <Link className="btn btn-ghost" to="/browse-products">Continue shopping</Link>
              </div>

              {totals.subtotal < FREE_DELIVERY_THRESHOLD && liveProducts.length > 0 && (
                <Recommendations
                  cartItemIds={cartItemIds}
                  subtotal={totals.subtotal}
                  liveProducts={liveProducts}
                  onCartChange={refreshCart}
                />
              )}
            </div>

            {/* ── Right: summary ── */}
            <aside className="cart-summary">
              <h3 className="h3" style={{ marginTop: 0 }}>Order summary</h3>

              <DeliveryCountdown placement="cart" style={{ marginBottom: "var(--s-3)" }} />

              <PromoCodeBox
                subtotal={totals.subtotal}
                deliveryCost={totals.delivery}
                appliedCode={promoCode?.code}
                onApply={setPromoCode}
                onRemove={() => setPromoCode(null)}
              />

              <FreeDeliveryBar subtotal={totals.subtotal} />

              <div className="sum-row" style={{ marginTop: "var(--s-4)" }}>
                <span>Subtotal</span>
                <strong>£{totals.subtotal.toFixed(2)}</strong>
              </div>
              <div className="sum-row">
                <span>Delivery</span>
                <strong>{totals.delivery ? `£${totals.delivery.toFixed(2)}` : "Free"}</strong>
              </div>
              {totals.lateFee > 0 && (
                <div className="sum-row">
                  <span>Late delivery</span>
                  <strong>£{totals.lateFee.toFixed(2)}</strong>
                </div>
              )}
                {totals.discount > 0 && (
                  <div className="sum-row" style={{ color: '#276749' }}>
                    <span>Discount ({promoCode.code})</span>
                    <strong>−£{totals.discount.toFixed(2)}</strong>
                  </div>
                )}
                {promoCode?.freeDelivery && (
                  <div className="sum-row" style={{ color: '#276749', fontSize: 12 }}>
                    <span>Free delivery applied</span>
                    <strong>✓</strong>
                  </div>
                )}
              <hr className="sum-sep" />
              <div className="sum-row sum-total">
                <span>Total</span>
                <strong>£{totals.grand.toFixed(2)}</strong>
              </div>

              {hasStockProblem && (
                <div className="notice" style={{ marginTop: "var(--s-4)", borderColor: "#f1b500", background: "#fff9e6", fontSize: 13, gap: "var(--s-2)" }}>
                  <span>🔒</span>
                  <span style={{ color: "#7a5a0a", fontWeight: 600 }}>Resolve stock issues to continue</span>
                </div>
              )}

              {/* ── Delivery instructions ───────────────────────────────── */}
              <div style={{ marginTop: "var(--s-4)" }}>
                <label htmlFor="delivery-instructions" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-2)", color: "var(--text)" }}>
                  Delivery instructions <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span>
                </label>
                <textarea
                  id="delivery-instructions"
                  rows={3}
                  maxLength={300}
                  placeholder="e.g. Leave at the side gate, ring the bell twice…"
                  value={deliveryInstructions}
                  onChange={e => setDeliveryInstructions(e.target.value)}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "var(--s-3)", fontSize: 13,
                    border: "1px solid var(--border)", borderRadius: "var(--r-sm)",
                    resize: "vertical", fontFamily: "inherit",
                    background: "var(--surface)", color: "var(--text)",
                    lineHeight: 1.5,
                  }}
                />
                {deliveryInstructions.length > 0 && (
                  <p style={{ fontSize: 11, color: "var(--muted)", textAlign: "right", margin: "var(--s-1) 0 0" }}>
                    {deliveryInstructions.length}/300
                  </p>
                )}
              </div>

              {/* ── Delivery time slot ──────────────────────────────────── */}
              <div style={{ marginTop: "var(--s-4)" }}>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: "var(--s-2)", color: "var(--text)" }}>
                  Delivery window <span style={{ color: "#e53e3e" }}>*</span> 
                  {!deliverySlot && <span style={{ fontWeight: 400, color: "#e53e3e", fontSize: 11, marginLeft: 8 }}>(Required)</span>}
                </p>
                
                <div style={{ 
                  display: "grid", 
                  gap: "var(--s-2)",
                  padding: "4px",
                  borderRadius: "var(--r-sm)",
                  border: !deliverySlot ? "1px dashed #feb2b2" : "1px solid transparent" // Subtle hint if empty
                }}>
                  {[
                    { value: "morning", label: "Late morning",  sub: "10:30am – 1:00pm" },
                    { value: "evening", label: "Early evening", sub: "5:30pm – 7:00pm"   },
                    { value: "late",    label: "Late evening",  sub: "7:00pm – 9:00pm · +£1.00 surcharge" },
                  ].map(opt => {
                    const deferred = slotDeferred(opt.value);
                    const arrives  = formatResume(slotDeliveryDate(opt.value));
                    return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setDeliverySlot(opt.value); if (opt.value !== "late") setLateNote(""); }}
                      style={{
                        textAlign: "left", padding: "var(--s-3)",
                        border: `2px solid ${deliverySlot === opt.value ? "var(--brand-2, #4CAF50)" : "var(--border)"}`,
                        borderRadius: "var(--r-sm)",
                        background: deliverySlot === opt.value ? "var(--brand-2-light, #f0fff0)" : "var(--surface)",
                        cursor: "pointer", transition: "border-color 0.15s",
                      }}
                      aria-pressed={deliverySlot === opt.value}
                    >
                      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{opt.label}</span>
                      <span style={{ display: "block", fontSize: 11, color: opt.value === "late" && deliverySlot !== "late" ? "#b45309" : "var(--muted)", marginTop: 2 }}>
                        {deferred && arrives ? `${opt.sub} · resumes ${arrives}` : opt.sub}
                      </span>
                    </button>
                    );
                  })}
                </div>

                {deliveryStatus?.anyDeferred && (
                  <div className="notice" style={{ marginTop: "var(--s-3)", borderColor: "#c3e6cb", background: "#eef7f0", flexDirection: "column", alignItems: "flex-start", gap: "var(--s-1)" }}>
                    <strong style={{ color: "#1a6e35" }}>🌿 Deliveries are temporarily paused</strong>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>
                      {deliveryStatus.message ? `${deliveryStatus.message} ` : ""}
                      Order as normal.{" "}
                      {slotDeliveryDate(deliverySlot || "evening")
                        ? `Your order will be delivered when deliveries resume on ${formatResume(slotDeliveryDate(deliverySlot || "evening"))}.`
                        : "Your order will be delivered once deliveries resume."}
                    </span>
                  </div>
                )}

                {deliverySlot === "late" && (
                  <div style={{ marginTop: "var(--s-3)" }}>
                    <label htmlFor="late-note" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>
                      Preferred time or note <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span>
                    </label>
                    <input
                      id="late-note"
                      type="text"
                      placeholder="e.g. closer to 8pm works best"
                      value={lateNote}
                      onChange={e => setLateNote(e.target.value)}
                      style={{
                        width: "100%", boxSizing: "border-box",
                        padding: "var(--s-3)", fontSize: 13,
                        border: "1px solid var(--border)", borderRadius: "var(--r-sm)",
                        fontFamily: "inherit", background: "var(--surface)", color: "var(--text)",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* ── Payment method ──────────────────────────────────────── */}
              <div style={{ marginTop: "var(--s-4)" }}>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: "var(--s-2)", color: "var(--text)" }}>
                  Payment method
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-2)" }}>
                  {[
                    { value: "card", label: "💳 Pay by card",      sub: "Secure checkout via Stripe" },
                    { value: "cash", label: "💵 Cash on delivery", sub: "Pay when Michael arrives"   },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setPaymentMethod(opt.value); setErr(""); setPhoneErr(""); }}
                      style={{
                        textAlign: "left", padding: "var(--s-3)",
                        border: `2px solid ${paymentMethod === opt.value ? "var(--brand-2, #4CAF50)" : "var(--border)"}`,
                        borderRadius: "var(--r-sm)", background: paymentMethod === opt.value ? "var(--brand-2-light, #f0fff0)" : "var(--surface)",
                        cursor: "pointer", transition: "border-color 0.15s",
                      }}
                      aria-pressed={paymentMethod === opt.value}
                    >
                      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{opt.label}</span>
                      <span style={{ display: "block", fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Cash-only delivery details form ────────────────────── */}
              {paymentMethod === "cash" && (
                <div style={{ marginTop: "var(--s-3)", display: "grid", gap: "var(--s-3)" }}>
                  <p style={{ fontSize: 13, color: "var(--muted)", margin: 0 }}>
                    Enter your delivery details so Michael knows where to bring your order.
                  </p>

                  <div>
                    <label htmlFor="cash-name" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>Full name<span style={{ color: "#e53e3e" }}> *</span></label>
                    <input id="cash-name" type="text" autoComplete="name" placeholder="Jane Smith" required
                      value={cashName} onChange={e => setCashName(e.target.value)}
                      style={fieldStyle()} />
                  </div>

                  <div>
                    <label htmlFor="cash-phone" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>Mobile number<span style={{ color: "#e53e3e" }}> *</span></label>
                    <input id="cash-phone" type="tel" autoComplete="tel" placeholder="07700 900000"
                      value={cashPhone} onChange={e => { setCashPhone(e.target.value); setPhoneErr(""); }}
                      style={fieldStyle(!!phoneErr)} />
                    {phoneErr && <p style={{ fontSize: 12, color: "#e53e3e", margin: "var(--s-1) 0 0" }}>{phoneErr}</p>}
                  </div>

                  <div>
                    <label htmlFor="cash-email" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>Email address<span style={{ color: "#e53e3e" }}> *</span></label>
                    <input id="cash-email" type="email" autoComplete="email" placeholder="jane@example.com" required
                      value={cashEmail} onChange={e => setCashEmail(e.target.value)}
                      style={fieldStyle()} />
                  </div>

                  <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: 0 }} />

                  <div>
                    <label htmlFor="cash-line1" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>Address line 1<span style={{ color: "#e53e3e" }}> *</span></label>
                    <input id="cash-line1" type="text" autoComplete="address-line1" placeholder="12 Example Street"
                      value={cashLine1} onChange={e => setCashLine1(e.target.value)}
                      style={fieldStyle()} />
                  </div>

                  <div>
                    <label htmlFor="cash-line2" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>
                      Address line 2 <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span>
                    </label>
                    <input id="cash-line2" type="text" autoComplete="address-line2" placeholder="Flat 3, Annexe, etc."
                      value={cashLine2} onChange={e => setCashLine2(e.target.value)}
                      style={fieldStyle()} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--s-2)" }}>
                    <div>
                      <label htmlFor="cash-city" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>Town / city<span style={{ color: "#e53e3e" }}> *</span></label>
                      <input id="cash-city" type="text" autoComplete="address-level2" placeholder="Hockley"
                        value={cashCity} onChange={e => setCashCity(e.target.value)}
                        style={fieldStyle()} />
                    </div>
                    <div>
                      <label htmlFor="cash-postcode" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: "var(--s-1)", color: "var(--text)" }}>Postcode<span style={{ color: "#e53e3e" }}> *</span></label>
                      <input id="cash-postcode" type="text" autoComplete="postal-code" placeholder="SS5 4AA"
                        value={cashPostcode} onChange={e => setCashPostcode(e.target.value.toUpperCase())}
                        style={{ ...fieldStyle(), width: 90, textTransform: "uppercase" }} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Allergen notice ───────────────────────────────────── */}
<div
  role="alert"
  aria-live="polite"
  style={{
    marginTop: "var(--s-4)",
    padding: "var(--s-3)",
    borderRadius: "var(--r-sm)",
    border: "1px solid #f6c453",
    background: "#fff8e8",
    display: "grid",
    gap: "6px",
  }}
>
  <div style={{ fontSize: 16, fontWeight: 700, color: "#7a5a0a" }}>
    ⚠ Allergen information - Mustard
  </div>

  <p
    style={{
      margin: 0,
      fontSize: 13,
      lineHeight: 1.5,
      color: "var(--text)",
    }}
  >
    Some products may contain or come into contact with mustard
    during growing, harvesting, or packing. If you have a mustard allergy or
    severe food allergy, please contact us before ordering.
  </p>

  <a
    href="tel:07541264513"
    style={{
      fontSize: 13,
      fontWeight: 600,
      color: "var(--brand-2)",
      textDecoration: "none",
    }}
  >
    📞 07541 264513
  </a>
</div>

              <TrustBlock />

              <p className="muted" style={{ fontSize: 13, marginTop: "var(--s-3)" }}>
                Delivering fresh across <strong style={{ color: "var(--text)" }}>Hockley, Rayleigh, Rochford</strong> and surrounding SS postcodes.
              </p>

              {/* ── Mailing list opt-in ───────────────────────────────── */}
              <label
                htmlFor="cart-mailing-list"
                style={{
                  display: "flex", gap: "var(--s-2)", alignItems: "flex-start",
                  marginTop: "var(--s-3)", padding: "var(--s-3)",
                  borderRadius: "var(--r-sm)", border: "1px solid var(--border)",
                  background: "var(--panel-2)", cursor: "pointer",
                }}
              >
                <input
                  id="cart-mailing-list"
                  type="checkbox"
                  checked={joinMailingList}
                  onChange={e => setJoinMailingList(e.target.checked)}
                  style={{ width: 18, height: 18, marginTop: 1, flex: "0 0 auto", accentColor: "var(--brand-2)" }}
                />
                <span style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text)" }}>
                  <strong>Email me harvest updates</strong>
                  <span className="muted" style={{ display: "block", fontSize: 12 }}>
                    New varieties, what's ready this week, and the occasional offer. Untick if you'd rather not, and you can unsubscribe from any email.
                  </span>
                </span>
              </label>

              <button
                className={`btn sum-btn ${canCheckout ? "btn-primary" : "btn-ghost"}`}
                onClick={checkout}
                disabled={!canCheckout}
                aria-busy={checkingOut}
                style={{ opacity: !canCheckout ? 0.55 : 1, cursor: !canCheckout ? "not-allowed" : "pointer", marginTop: "var(--s-4)" }}
              >
                {checkingOut
                  ? (paymentMethod === "cash" ? "Placing order…" : "Redirecting…")
                  : hasStockProblem
                    ? "Fix stock issues first"
                    : paymentMethod === "cash"
                      ? "Place cash order"
                      : "Continue to secure payment"}
              </button>

              <p className="muted" style={{ fontSize: 12, marginTop: "var(--s-2)", textAlign: "center" }}>
                {hasStockProblem
                  ? "Update quantities above to match available stock."
                  : deliveryStatus?.anyDeferred && slotDeliveryDate(deliverySlot || "evening")
                    ? `Deliveries are temporarily paused, and resume on ${formatResume(slotDeliveryDate(deliverySlot || "evening"))}.`
                    : paymentMethod === "cash"
                      ? "Michael will confirm your delivery time by phone or email."
                      : "Free local delivery on orders of £6.99 or more."}
              </p>
            </aside>
          </div>
        )}
      </div>
    </Layout>
  );
}